/**
 * Singleton managing the Twilio Voice Device lifecycle.
 *
 * Plain JS class with an event-emitter pattern so it works with Vue 3 Options
 * API components (no reactive refs — components subscribe via on/off).
 *
 * Lifecycle: initialize() creates the Device but does NOT register it.
 * Call register()/unregister() to control availability for inbound calls.
 *
 * Cross-tab sync: tabs share the user's intent ("I want to be available"),
 * and each tab registers when another tab broadcasts it. Every tab shows the
 * same status, built from what each tab reports about itself: busy if any tab
 * is on a call, available if any tab holds a registered Device, else offline.
 * A tab that stops reporting (closed, crashed, asleep) stops counting.
 *
 * Usage:
 *   import { twilioDevice } from '@/services/TwilioDeviceService'
 *   await twilioDevice.initialize()       // after auth
 *   await twilioDevice.register()         // user goes "available"
 *   twilioDevice.on('incoming', handler)  // in component mounted()
 */
import { z } from 'zod'
import {
  getInboundCallByCallSid,
  getOutboundCallStatus,
  getVoiceToken,
  getUnreadVoicemailCount,
  lookupByPhone,
  markProviderDecline,
} from '@/services/api/voiceApi'
import { BrowserStore } from '@/utils/BrowserStore'
import {
  describeAudioTrack,
  logTelehealthAudioDiagnostic,
} from '@/services/telehealthAudioDiagnostics'
import { showCallNotification } from '@/services/callNotifications'
import { dismissToast, notifyLoadError, showSuccessToast } from '@/utils/loadError'
import { PHONE_STATUS_CHANNEL, phoneAvailabilityStore } from '@/services/phoneSession'
import { RnnoiseAudioProcessor, shouldUseNoiseSuppression } from '@/services/noiseSuppression'
import { DEVICE_PREFS_KEY, findSavedDevice, loadDevicePrefs, updateDevicePrefs } from '@/services/devicePrefs'

const CONNECTION_TOAST_ID = 'phone-connection'

const STATUS_CHANNEL = PHONE_STATUS_CHANNEL
const VOICEMAIL_POLL_INTERVAL = 120000
const INBOUND_SESSION_RETRY_MS = 500
const INBOUND_SESSION_MAX_ATTEMPTS = 10
const PHONE_SESSION_RETRY_MS = 500
const PHONE_SESSION_MAX_ATTEMPTS = 20
// The first delay gives the SDK time to re-register on its own after a
// signaling drop before we replace the Device. The last delay repeats.
const RECOVERY_DELAYS_MS = [10000, 5000, 15000, 30000, 60000]
const PRESENCE_INTERVAL_MS = 5000
const PRESENCE_TTL_MS = 15000

// Keeps the saved id and name current: fills in the name for settings saved
// before names were kept, and follows a headset to its new id when the
// browser renumbers it. Writes only when something changed.
function rememberDevice(kind, device) {
  updateDevicePrefs({ [`${kind}DeviceId`]: device.deviceId, [`${kind}DeviceLabel`]: device.label || '' })
}

// Right after start-up the SDK hasn't listed the connected devices yet, and
// it doesn't announce that first list. Without this, a saved device looks
// unplugged and the phone stays on the system default.
async function waitForDeviceList(audio, timeoutMs = 3000) {
  const listed = () => audio.availableOutputDevices?.size || audio.availableInputDevices?.size
  for (let waited = 0; !listed() && waited < timeoutMs; waited += 100) {
    await new Promise((resolve) => setTimeout(resolve, 100))
  }
}

const voicemailStore = new BrowserStore(
  'unreadVoicemailCache',
  z.object({ count: z.number().default(0), ts: z.number().default(0) }),
)

class TwilioDeviceService {
  constructor() {
    /** @type {Device | null} */
    this.device = null
    /** @type {import('@twilio/voice-sdk').Call | null} */
    this.activeCall = null
    this.currentCallContext = null
    /** @type {string | null} */
    this.identity = null
    this._listeners = {}
    this._tokenRefreshTimer = null
    this._initialized = false
    this._registered = false
    this._isDestroying = false
    this._initializationPromise = null
    this._registrationPromise = null
    this._recoveryPromise = null
    this._recoveryTimer = null
    this._recoveryAttempt = 0
    // One toast and one Sentry warning per outage, not one per retry.
    this._connectionErrorReported = false
    this._lastConnectionError = null
    // Set when the voice token request says the user is logged out.
    this._authLost = false
    /** @type {Notification | null} */
    this._incomingNotification = null

    /** This tab's own state. @type {'offline' | 'available' | 'busy'} */
    this._ownStatus = 'offline'
    /** What every tab shows. @type {'offline' | 'available' | 'busy'} */
    this._status = 'offline'
    this._tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    /** @type {Map<string, { status: string, at: number }>} */
    this._peers = new Map()
    this._unreadVoicemails = voicemailStore.load().count
    this._voicemailPollTimer = null
    this._channel = new BroadcastChannel(STATUS_CHANNEL)
    this._channel.onmessage = (e) => this._onCrossTabMessage(e.data)
    this._onStorageEvent = (e) => {
      // Fallback for tabs that missed the BroadcastChannel intent message.
      if (e.key === 'twilioPhoneAvailability' && e.newValue) {
        try {
          const { available } = JSON.parse(e.newValue)
          if (typeof available === 'boolean') this._applyAvailabilityIntent(available)
        } catch {
          // ignore
        }
      }
      // "Reduce background noise" flipped in another tab (usually Phone
      // Settings) must reach the call running in this one.
      if (e.key === DEVICE_PREFS_KEY) {
        this.applyNoiseSuppression()
        // Mic or speaker picked in another tab.
        this.applyAudioDevices({ includeMic: !!this.activeCall })
      }
      if (e.key !== 'unreadVoicemailCache' || !e.newValue) return
      try {
        const { count } = JSON.parse(e.newValue)
        if (typeof count === 'number' && count !== this._unreadVoicemails) {
          this._unreadVoicemails = count
          this._emit('unreadVoicemailsChanged', count)
        }
      } catch {
        // ignore
      }
    }
    window.addEventListener('storage', this._onStorageEvent)
    this._onOnline = () => {
      if (!this._shouldRecover()) return
      this._clearRecoveryTimer()
      this._recoverNow()
    }
    this._presenceTimer = null
    this._onPageHide = () => {
      this._channel.postMessage({ type: 'presence', tabId: this._tabId, status: 'offline' })
    }
  }

  // Started when the phone initializes (or another tab reports in), not when
  // this module loads: App.vue imports the service for every user at boot.
  _startPresence() {
    if (this._presenceTimer) return
    this._presenceTimer = setInterval(() => {
      this._publishPresence()
      this._recomputeStatus()
    }, PRESENCE_INTERVAL_MS)
    window.addEventListener('online', this._onOnline)
    window.addEventListener('pagehide', this._onPageHide)
  }

  // ─── Event emitter ──────────────────────────────────────────────────────

  /**
   * @param {string} event
   * @param {Function} fn
   */
  on(event, fn) {
    if (!this._listeners[event]) this._listeners[event] = []
    this._listeners[event].push(fn)
  }

  /**
   * @param {string} event
   * @param {Function} fn
   */
  off(event, fn) {
    const list = this._listeners[event]
    if (!list) return
    this._listeners[event] = list.filter((f) => f !== fn)
  }

  /** @param {string} event @param {...any} args */
  _emit(event, ...args) {
    const list = this._listeners[event]
    if (!list) return
    for (const fn of list) {
      try {
        fn(...args)
      } catch (e) {
        console.error(`[TwilioDevice] listener error on "${event}":`, e)
      }
    }
  }

  // ─── Cross-tab status sync ──────────────────────────────────────────────

  /** @returns {'offline' | 'available' | 'busy'} */
  get status() {
    return this._status
  }

  /** Sets this tab's own state; the displayed status also counts other tabs. */
  _setStatus(status) {
    if (this._ownStatus === status) return
    // Never downgrade from busy while a call is active
    if (this._ownStatus === 'busy' && status !== 'busy' && this.activeCall) return
    this._ownStatus = status
    this._publishPresence()
    this._recomputeStatus()
  }

  _publishPresence() {
    if (this._isDestroying) return
    this._channel.postMessage({ type: 'presence', tabId: this._tabId, status: this._ownStatus })
  }

  _recomputeStatus() {
    const now = Date.now()
    for (const [tabId, peer] of this._peers) {
      if (now - peer.at > PRESENCE_TTL_MS) this._peers.delete(tabId)
    }
    const statuses = [this._ownStatus, ...[...this._peers.values()].map((p) => p.status)]
    let status = 'offline'
    if (statuses.includes('busy')) status = 'busy'
    else if (statuses.includes('available')) status = 'available'
    if (status === this._status) return
    this._status = status
    this._emit('statusChanged', status)
    if (status === 'offline') {
      this._showConnectionToastIfNoTabCanRing()
    } else if (this._connectionErrorReported && !this._registered) {
      // Another tab can take calls again, so the voicemail warning no longer holds.
      dismissToast(CONNECTION_TOAST_ID)
      this._connectionErrorReported = false
    }
  }

  _localStatus() {
    if (this.activeCall) return 'busy'
    return this._registered ? 'available' : 'offline'
  }

  _broadcastAvailabilityIntent(available) {
    this._channel.postMessage({ type: 'intent', available })
  }

  _applyAvailabilityIntent(available) {
    if (available && this._initialized && !this._registered && !this.activeCall) {
      this.register().catch(() => {})
    }
    if (!available && this._registered) {
      this.unregister({ broadcast: false })
    }
  }

  // ─── Unread voicemail count ──────────────────────────────────────────

  get unreadVoicemails() {
    return this._unreadVoicemails
  }

  _setUnreadVoicemails(count) {
    if (this._unreadVoicemails === count) return
    this._unreadVoicemails = count
    voicemailStore.save({ count, ts: Date.now() })
    this._emit('unreadVoicemailsChanged', count)
  }

  async fetchUnreadVoicemails() {
    try {
      const count = await getUnreadVoicemailCount()
      this._setUnreadVoicemails(count)
    } catch {
      // silently ignore — polling will retry
    }
  }

  startVoicemailPolling() {
    this.stopVoicemailPolling()
    const cached = voicemailStore.load()
    if (Date.now() - cached.ts < VOICEMAIL_POLL_INTERVAL) {
      this._unreadVoicemails = cached.count
      this._emit('unreadVoicemailsChanged', cached.count)
    } else {
      this.fetchUnreadVoicemails()
    }
    this._voicemailPollTimer = setInterval(
      () => this.fetchUnreadVoicemails(),
      VOICEMAIL_POLL_INTERVAL,
    )
  }

  stopVoicemailPolling() {
    if (this._voicemailPollTimer) {
      clearInterval(this._voicemailPollTimer)
      this._voicemailPollTimer = null
    }
  }

  _broadcastCallContext() {
    if (this.activeCall && this.currentCallContext) {
      const context = {
        callerNumber: this.activeCall.parameters?.From
          || this.activeCall.customParameters?.get('to_phone_number')
          || '',
        workId: this.currentCallContext.workId,
        entityName: this.currentCallContext.entityName,
        sessionUuid: this.currentCallContext.sessionUuid || null,
      }
      try { localStorage.setItem('twilioActiveCallContext', JSON.stringify(context)) } catch {}
      this._channel.postMessage({ type: 'callContext', context })
    } else {
      try { localStorage.removeItem('twilioActiveCallContext') } catch {}
      this._channel.postMessage({ type: 'callContext', context: null })
    }
  }

  requestFocusCallTab() {
    this._channel.postMessage({ type: 'command', action: 'focus' })
  }

  _onCrossTabMessage(data) {
    if (data.type === 'intent') {
      this._applyAvailabilityIntent(data.available)
    }
    if (data.type === 'presence' && data.tabId !== this._tabId) {
      this._startPresence()
      if (data.status === 'offline') this._peers.delete(data.tabId)
      else this._peers.set(data.tabId, { status: data.status, at: Date.now() })
      this._recomputeStatus()
    }
    if (data.type === 'callContext') {
      if (!this.activeCall) {
        this._emit('crossTabCall', data.context)
      }
    }
    if (data.type === 'command') {
      if (data.action === 'focus' && this.activeCall) {
        // Chrome ignores window.focus() here because the click happened in
        // another tab; clicking a notification from this tab is allowed to.
        window.focus()
        showCallNotification({ title: 'Call in progress', body: 'Click to go back to your call.', tag: 'kairon-call-tab' })
      }
      if (data.action === 'register' && this._initialized && !this._registered) {
        this.register()
      }
      if (data.action === 'unregister' && this._registered) {
        this.unregister()
      }
      if (data.action === 'rebroadcastContext' && this.activeCall) {
        this._broadcastCallContext()
      }
    }
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  async initialize({ force = false, restoreAvailability = true } = {}) {
    if (this._initialized && !force) return
    if (this._initializationPromise) return this._initializationPromise

    this._initializationPromise = this._initialize({ force, restoreAvailability })
    try {
      await this._initializationPromise
    } finally {
      this._initializationPromise = null
    }
  }

  async _initialize({ force, restoreAvailability }) {
    this._isDestroying = false
    this._startPresence()
    if (force) this._discardDevice()
    try {
      const { token, identity } = await getVoiceToken()
      this.identity = identity

      const { Device } = await import('@twilio/voice-sdk')
      this.device = new Device(token, {
        edge: 'ashburn',
        closeProtection: true,
        logLevel: 'warn',
        codecPreferences: ['opus', 'pcmu'],
        allowIncomingWhileBusy: true,
      })
      // The SDK lists the connected devices shortly after start-up, and again
      // whenever one is plugged in or removed. Apply the saved ones each time.
      // Subscribe before any await, or the first list can arrive unheard.
      this.device.audio?.on?.('deviceChange', () => this.applyAudioDevices({ includeMic: !!this.activeCall }))
      this._bindDeviceEvents()
      await this.applyNoiseSuppression()
      await this.applyAudioDevices()
      this._initialized = true
      this.startVoicemailPolling()

      const stored = phoneAvailabilityStore.load()
      if (restoreAvailability && stored.available) {
        // Loading the application is not a user gesture. Surface the failure
        // through deviceError, and keep retrying so a reload never leaves
        // someone who chose to be available stuck offline.
        this.register().catch(() => this._scheduleRecovery())
      }
    } catch (e) {
      console.error('[TwilioDevice] initialization failed:', e)
      this._reportConnectionError(e, 'voice-token')
      if (restoreAvailability) this._scheduleRecovery()
      this._emit('deviceError', e)
      throw e
    }
  }

  _discardDevice() {
    const device = this.device
    // Clear the reference before destroying the Device. Its asynchronous
    // events then cannot overwrite the state of a newly-created Device.
    this.device = null
    this._initialized = false
    this._registered = false
    this._noiseProcessor = null
    if (!device) return
    try {
      device.destroy()
    } catch (e) {
      console.warn('[TwilioDevice] could not discard stale device:', e)
    }
  }

  _markRegistered() {
    const wasRegistered = this._registered
    this._registered = true
    this._recoveryAttempt = 0
    if (this._connectionErrorReported) {
      dismissToast(CONNECTION_TOAST_ID)
      showSuccessToast('Your phone is reconnected.', { toastId: 'phone-reconnected' })
    }
    this._connectionErrorReported = false
    this._lastConnectionError = null
    this._authLost = false
    this._clearRecoveryTimer()
    this._setStatus(this._localStatus())
    phoneAvailabilityStore.save({ available: true })
    if (!wasRegistered) {
      this._emit('deviceRegistered', { identity: this.identity })
    }
  }

  /** Toast + Sentry warning through the shared load-error flow, once per outage. */
  _reportConnectionError(error, endpoint) {
    const status = error?.response?.status
    if (status === 401) {
      // Session expired: retrying can't help, and the user has to log in again.
      this._authLost = true
      this.endSession()
      return
    }
    if (status === 403) {
      this._authLost = true
      return
    }
    this._lastConnectionError = { error, endpoint }
    this._showConnectionToastIfNoTabCanRing()
  }

  /**
   * The toast says calls go to voicemail, so it only shows when that is true:
   * this tab failed to reconnect and no other tab is registered either.
   */
  _showConnectionToastIfNoTabCanRing() {
    if (!this._lastConnectionError || this._connectionErrorReported || this._status !== 'offline') return
    this._connectionErrorReported = true
    const { error, endpoint } = this._lastConnectionError
    // Missed calls are costly, so this toast stays until it is dismissed or the phone reconnects.
    notifyLoadError(error, {
      feature: 'phone',
      endpoint,
      message: "Your phone isn't connected, so calls are going to voicemail. Reconnecting...",
      toastId: CONNECTION_TOAST_ID,
      toastOptions: { timeout: false, closeOnClick: false, draggable: false, showCloseButtonOnHover: false },
    })
  }

  _reportRegistrationFailure(error) {
    console.error('[TwilioDevice] registration failed:', error)
    console.error('[TwilioDevice] registration error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      causes: error.causes,
    })
    this._setStatus('offline')
    this._reportConnectionError(error, 'twilio-register')
    this._emit('deviceError', error)
  }

  async register() {
    if (this._registered) {
      this._markRegistered()
      return
    }
    if (this._registrationPromise) return this._registrationPromise

    this._authLost = false
    this._registrationPromise = this._register({ retryWithFreshDevice: true })
    try {
      await this._registrationPromise
    } finally {
      this._registrationPromise = null
    }
    this._broadcastAvailabilityIntent(true)
  }

  async _register({ retryWithFreshDevice }) {
    try {
      if (!this.device) await this.initialize()
      if (!this.device) throw new Error('Twilio Device could not be initialized')
      await this.device.register()
      this._markRegistered()
    } catch (error) {
      if (retryWithFreshDevice && !this.activeCall) {
        console.warn('[TwilioDevice] retrying registration with a fresh token')
        try {
          await this.initialize({ force: true, restoreAvailability: false })
          return await this._register({ retryWithFreshDevice: false })
        } catch (retryError) {
          this._reportRegistrationFailure(retryError)
          throw retryError
        }
      }
      this._reportRegistrationFailure(error)
      throw error
    }
  }

  async unregister({ broadcast = true } = {}) {
    this._clearRecoveryTimer()
    if (!this.device || !this._registered) {
      phoneAvailabilityStore.save({ available: false })
      if (broadcast) this._broadcastAvailabilityIntent(false)
      return
    }
    try {
      this._registered = false
      this._setStatus('offline')
      phoneAvailabilityStore.save({ available: false })
      if (broadcast) this._broadcastAvailabilityIntent(false)
      this.device.unregister()
      this._emit('deviceUnregistered')
    } catch (e) {
      console.error('[TwilioDevice] unregistration failed:', e)
    }
  }

  _bindDeviceEvents() {
    const device = this.device
    if (!device) return

    device.on('incoming', (call) => {
      if (this.device !== device) return
      if (this.activeCall) {
        call.reject()
        return
      }
      this.activeCall = call
      this._bindCallEvents(call)

      const callSid = call.parameters?.CallSid || null
      const fromNumber = call.parameters?.From

      this.currentCallContext = {
        direction: 'inbound',
        callSid,
        sessionUuid: null,
        workId: null,
        entityId: null,
        entityName: null,
        matchCount: 0,
        matchedEntities: [],
        phoneBookEntry: null,
        status: 'pending',
      }

      this._setStatus('busy')

      // The tab you are looking at already shows the incoming call panel.
      if (!document.hasFocus()) {
        this._incomingNotification = showCallNotification({
          title: 'Incoming call',
          body: 'Click to answer in Kairon.',
          tag: 'kairon-incoming-call',
        })
      }

      this._emit('incoming', {
        call,
        callSid,
        matchCount: 0,
        matchedEntities: [],
        fromNumber,
        lookupLoading: true,
      })

      if (fromNumber) {
        lookupByPhone(fromNumber).then((result) => {
          const phoneBookEntry = result.phone_book_entry || null
          const matchedEntities = phoneBookEntry ? [] : result.matches || []
          const matchCount = phoneBookEntry ? 0 : result.match_count || 0
          this.currentCallContext = {
            ...this.currentCallContext,
            entityId: matchCount === 1 ? matchedEntities[0]?.entity_id : null,
            entityName: matchCount === 1 ? matchedEntities[0]?.entity_name : null,
            matchCount,
            matchedEntities,
            phoneBookEntry,
          }
          this._emit('incomingLookupComplete', { matchCount, matchedEntities, phoneBookEntry })
        }).catch(() => {
          this._emit('incomingLookupComplete', { matchCount: 0, matchedEntities: [], phoneBookEntry: null })
        })
      } else {
        this._emit('incomingLookupComplete', { matchCount: 0, matchedEntities: [], phoneBookEntry: null })
      }
    })

    device.on('registered', () => {
      if (this.device === device) this._markRegistered()
    })

    device.on('tokenWillExpire', async () => {
      try {
        const { token } = await getVoiceToken()
        if (this.device === device) device.updateToken(token)
      } catch (e) {
        console.error('[TwilioDevice] token refresh failed:', e)
        if (this.device !== device) return
        // The expiring token will drop the registration; don't keep showing
        // available until then. Recovery fetches a fresh token.
        this._registered = false
        this._setStatus('offline')
        this._reportConnectionError(e, 'voice-token')
        this._emit('deviceError', e)
        this._scheduleRecovery()
      }
    })

    device.on('error', (err) => {
      if (this.device !== device) return
      console.error('[TwilioDevice] device error:', err)
      console.error('[TwilioDevice] device error details:', {
        name: err.name,
        message: err.message,
        code: err.code,
        causes: err.causes,
      })
      this._emit('deviceError', err)
    })

    device.on('unregistered', () => {
      if (this.device !== device) return
      this._registered = false
      // A device can unregister because its tab is closing or it loses its
      // connection. That only affects this tab; only unregister() represents
      // the user's shared "go unavailable" action.
      this._setStatus('offline')
      this._emit('deviceUnregistered')
      this._scheduleRecovery()
    })

    device.on('destroyed', () => {
      if (this.device !== device) return
      this.device = null
      this._initialized = false
      this._registered = false
      this._setStatus('offline')
      this._scheduleRecovery()
    })
  }

  _shouldRecover() {
    return (
      !this._isDestroying &&
      !this._authLost &&
      !this._registered &&
      !this.activeCall &&
      !this._registrationPromise &&
      !this._recoveryPromise &&
      phoneAvailabilityStore.load().available
    )
  }

  _clearRecoveryTimer() {
    if (this._recoveryTimer) {
      clearTimeout(this._recoveryTimer)
      this._recoveryTimer = null
    }
  }

  /** Keep retrying, with backoff, while the user still wants to be available. */
  _scheduleRecovery() {
    if (this._recoveryTimer || !this._shouldRecover()) return
    const delays = RECOVERY_DELAYS_MS
    const delay = delays[Math.min(this._recoveryAttempt, delays.length - 1)]
    this._recoveryTimer = setTimeout(() => {
      this._recoveryTimer = null
      this._recoverNow()
    }, delay)
  }

  _recoverNow() {
    // The SDK may have re-registered the existing Device during the wait.
    if (!this._shouldRecover()) return
    this._recoveryAttempt += 1
    this._recoveryPromise = (async () => {
      try {
        await this.initialize({ force: true, restoreAvailability: false })
        await this._register({ retryWithFreshDevice: false })
      } catch {
        // _register and initialize have already emitted a deviceError.
      } finally {
        this._recoveryPromise = null
        if (!this._registered) this._scheduleRecovery()
      }
    })()
    return this._recoveryPromise
  }

  /** @param {import('@twilio/voice-sdk').Call} call */
  _bindCallEvents(call) {
    const guardUnload = () => {
      this._beforeUnloadHandler = (e) => { e.preventDefault() }
      window.addEventListener('beforeunload', this._beforeUnloadHandler)
    }
    const unguardUnload = () => {
      if (this._beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
        this._beforeUnloadHandler = null
      }
    }
    const closeIncomingNotification = () => {
      this._incomingNotification?.close()
      this._incomingNotification = null
    }
    const cleanupCall = () => {
      closeIncomingNotification()
      this._releaseMic()
      this.activeCall = null
      this.currentCallContext = null
      this._setStatus(this._registered ? 'available' : 'offline')
      this._broadcastCallContext()
      unguardUnload()
    }

    call.on('accept', () => {
      closeIncomingNotification()
      if (this.currentCallContext) {
        this.currentCallContext.callSid = call.parameters?.CallSid || null
        this.currentCallContext.connectedAt = Date.now()
      }
      this._setStatus('busy')
      guardUnload()
      this._broadcastCallContext()
      this._emit('callConnected', call)
      logTelehealthAudioDiagnostic('phone call input track', {
        direction: this.currentCallContext?.direction,
        track: describeAudioTrack(call.getLocalStream?.()?.getAudioTracks?.()[0]),
      })
      if (this.currentCallContext?.direction === 'inbound') {
        this._resolveInboundSession(call)
      }
    })

    call.on('volume', (inputVolume) => {
      this._emit('callVolume', inputVolume)
    })

    call.on('disconnect', () => {
      cleanupCall()
      this._emit('callDisconnected')
    })

    call.on('cancel', () => {
      let crossTabContext = null
      try {
        const stored = localStorage.getItem('twilioActiveCallContext')
        if (stored) crossTabContext = JSON.parse(stored)
      } catch {}

      closeIncomingNotification()
      this.activeCall = null
      this.currentCallContext = null
      this._broadcastCallContext()
      unguardUnload()
      this._setStatus(this._registered ? 'available' : 'offline')
      this._emit('callDisconnected')

      if (crossTabContext) {
        this._emit('crossTabCall', crossTabContext)
      }
      this._channel.postMessage({ type: 'command', action: 'rebroadcastContext' })
    })

    call.on('error', (err) => {
      console.error('[TwilioDevice] call error:', err)
      cleanupCall()
      this._emit('callError', err)
    })

    call.on('reject', () => {
      cleanupCall()
      this._emit('callDisconnected')
    })

    call.on('ringing', () => {
      this._emit('callRinging', call)
    })
  }

  // ─── Outbound ───────────────────────────────────────────────────────────

  /**
   * Place an outbound call via the Twilio Device.
   * @param {{ toPhoneNumber?: string, fromPhoneNumber: string, workId: string | number, entityId?: number | string | null, entityName?: string | null, phoneBookEntry?: import('./api/schema').components['schemas']['PhoneBookEntry'] | null, roleId?: number | null, contactLabel?: string | null, workRouteName?: string }} params
   * @returns {Promise<import('@twilio/voice-sdk').Call>}
   */
  async connect({ toPhoneNumber, fromPhoneNumber, workId, entityId, entityName, phoneBookEntry, roleId, contactLabel, workRouteName }) {
    if (!this.device) {
      throw new Error('Twilio Device not initialized')
    }
    if (this.activeCall) {
      throw new Error('A call is already active')
    }

    if (!this._registered) {
      await this.register()
    }

    const params = {
      to_phone_number: toPhoneNumber || phoneBookEntry?.phone_number,
      from_phone_number: fromPhoneNumber,
    }
    // String(undefined) used to reach the webhook as work_id='undefined'.
    if (workId != null && workId !== '') params.work_id = String(workId)
    if (phoneBookEntry) params.phone_book_entry_id = String(phoneBookEntry.id)
    else if (roleId) params.role_id = String(roleId)

    await this.applyNoiseSuppression()
    await this.applyAudioDevices({ includeMic: true })
    const call = await this.device.connect({ params, rtcConstraints: this._callMicConstraints() })

    this.activeCall = call
    this._bindCallEvents(call)

    this.currentCallContext = {
      direction: 'outbound',
      callSid: null,
      fromPhoneNumber: fromPhoneNumber || null,
      workId: workId || null,
      entityId: entityId || null,
      entityName: entityName || null,
      matchCount: -1,
      matchedEntities: [],
      phoneBookEntry: phoneBookEntry || null,
      contactLabel: contactLabel || null,
      workRouteName: workRouteName || 'ClinicalEngagement',
      status: workId ? 'linked' : 'pending',
    }

    this._setStatus('busy')
    this._emit('callConnecting', call)
    return call
  }

  // ─── Inbound ────────────────────────────────────────────────────────────

  /** @param {import('@twilio/voice-sdk').Call} call */
  async acceptIncoming(call) {
    // Don't make a ringing caller wait on loading the processor; the SDK
    // swaps it onto the live mic once it's ready. The chosen mic is quick to
    // open and must be in place before the call's audio starts.
    this.applyNoiseSuppression()
    await this.applyAudioDevices({ includeMic: true })
    call.accept({ rtcConstraints: this._callMicConstraints() })
  }

  /** @param {import('@twilio/voice-sdk').Call} call */
  async rejectIncoming(call) {
    const callSid = call.parameters?.CallSid
    if (callSid) {
      try {
        await markProviderDecline(callSid)
      } catch (e) {
        // The SDK still needs to reject the call if recording intent fails.
        // The backend will fall back to its normal busy handling in that case.
        console.error('[TwilioDevice] could not record provider decline:', e)
      }
    }
    call.reject()
    if (this.activeCall === call) {
      this.activeCall = null
    }
  }

  // ─── Phone session (conference) ─────────────────────────────────────────

  /**
   * Resolve the active call's phone session, retrying while the backend creates
   * it. Inbound sessions are filled in by _resolveInboundSession; outbound ones
   * are looked up by the provider leg's CallSid.
   * @returns {Promise<string | null>} the phone session uuid, or null if the call ended or it never appeared
   */
  async resolvePhoneSessionUuid() {
    const call = this.activeCall
    for (let attempt = 0; attempt < PHONE_SESSION_MAX_ATTEMPTS; attempt++) {
      const ctx = this.currentCallContext
      if (!call || this.activeCall !== call || !ctx) return null
      if (ctx.phoneSessionUuid) return ctx.phoneSessionUuid
      if (ctx.direction === 'outbound' && ctx.callSid) {
        try {
          const status = await getOutboundCallStatus(ctx.callSid)
          if (status?.phone_session_uuid && this.currentCallContext === ctx) {
            ctx.phoneSessionUuid = status.phone_session_uuid
            return ctx.phoneSessionUuid
          }
        } catch {
          // session not created yet
        }
      }
      await new Promise((resolve) => setTimeout(resolve, PHONE_SESSION_RETRY_MS))
    }
    return null
  }

  // ─── Inbound call session ───────────────────────────────────────────────

  /**
   * The browser only knows its own call leg's CallSid; the backend maps it to
   * the inbound phone session. The provider participant row is written right
   * after Twilio accepts the REST add, so the first lookups can miss.
   */
  /** @param {import('@twilio/voice-sdk').Call} call */
  async _resolveInboundSession(call) {
    const callSid = call.parameters?.CallSid
    if (!callSid) return
    for (let attempt = 0; attempt < INBOUND_SESSION_MAX_ATTEMPTS; attempt++) {
      if (this.activeCall !== call) return
      try {
        const inboundCall = await getInboundCallByCallSid({ call_sid: callSid })
        if (this.activeCall !== call || !this.currentCallContext) return
        this.currentCallContext.sessionUuid = inboundCall.uuid
        // Conference controls need the phone session and the Kairon number that
        // was dialled: it is the caller ID for anyone added to this call.
        this.currentCallContext.phoneSessionUuid = inboundCall.uuid
        this.currentCallContext.fromPhoneNumber = inboundCall.to_number || null
        this._broadcastCallContext()
        this._emit('inboundCallResolved', { sessionUuid: inboundCall.uuid })
        return
      } catch (e) {
        if (e?.response?.status !== 404) {
          console.error('[TwilioDevice] could not resolve inbound call session:', e)
          break
        }
      }
      await new Promise((resolve) => setTimeout(resolve, INBOUND_SESSION_RETRY_MS))
    }
    if (this.activeCall === call) {
      this._emit('inboundCallUnresolved')
    }
  }

  // ─── Engagement linking ──────────────────────────────────────────────────

  linkEngagement({ workId, entityId, entityName }) {
    if (this.currentCallContext) {
      this.currentCallContext.workId = workId
      this.currentCallContext.entityId = entityId
      this.currentCallContext.entityName = entityName
      this.currentCallContext.status = 'linked'
      this._broadcastCallContext()
      this._emit('engagementLinked', this.currentCallContext)
    }
  }

  // ─── Noise suppression ──────────────────────────────────────────────────

  /**
   * Adds or removes the RNNoise processor to match the tenant flag and the
   * user's preference. Safe to call again after either changes; the SDK
   * applies it to the mic on the next call (or live, during one).
   */
  async applyNoiseSuppression() {
    const audio = this.device?.audio
    if (!audio?.addProcessor) return
    const wanted = shouldUseNoiseSuppression()
    try {
      if (wanted && !this._noiseProcessor) {
        this._noiseProcessor = new RnnoiseAudioProcessor()
        await audio.addProcessor(this._noiseProcessor)
        // Two suppressors in a row smear speech; let RNNoise do it alone.
        // Echo cancellation and auto gain stay on. This covers a mic picked in
        // Phone Settings; _callMicConstraints covers the default mic.
        await audio.setAudioConstraints({ noiseSuppression: false })
      } else if (!wanted && this._noiseProcessor) {
        const processor = this._noiseProcessor
        this._noiseProcessor = null
        await audio.removeProcessor(processor)
        await audio.unsetAudioConstraints()
      }
    } catch (e) {
      // Calls still work without it, with the browser's own suppression.
      console.warn('[TwilioDevice] noise suppression unavailable:', e)
      this._noiseProcessor = null
    }
  }

  /**
   * getUserMedia constraints for a call's mic. The SDK applies
   * setAudioConstraints only to a mic chosen with setInputDevice; on the
   * default mic it opens a plain `audio: true` stream, which would leave the
   * browser's suppression running under RNNoise.
   */
  _callMicConstraints() {
    if (!this._noiseProcessor) return undefined
    return { audio: { noiseSuppression: false } }
  }

  // ─── Audio devices ──────────────────────────────────────────────────────

  /**
   * Points the Device at the mic and speaker saved in Phone Settings (or the
   * top bar). Picking a device only saves it; this is what applies it, so a
   * reload, a new tab or a reconnect doesn't fall back to the system default
   * while the dropdown still shows the headset.
   *
   * The speaker can be set any time. The mic only with `includeMic` (right
   * before or during a call): choosing a mic makes the SDK open it, and the
   * browser shows it as in use until _releaseMic().
   */
  async applyAudioDevices({ includeMic = false } = {}) {
    const audio = this.device?.audio
    if (!audio) return
    await waitForDeviceList(audio)
    const prefs = loadDevicePrefs()

    if (prefs.audioOutputDeviceId && audio.isOutputSelectionSupported) {
      const speaker = findSavedDevice(audio.availableOutputDevices?.values(), prefs.audioOutputDeviceId, prefs.audioOutputDeviceLabel)
      if (speaker) {
        rememberDevice('audioOutput', speaker)
        try {
          await audio.speakerDevices.set(speaker.deviceId)
        } catch (e) {
          console.warn('[TwilioDevice] could not use the chosen speaker:', e)
        }
      } else {
        console.warn('[TwilioDevice] chosen speaker is not connected; using the system default')
      }
    }

    const inputs = audio.availableInputDevices
    const mic = prefs.audioDeviceId ? findSavedDevice(inputs?.values(), prefs.audioDeviceId, prefs.audioDeviceLabel) : null
    if (mic) rememberDevice('audio', mic)
    if (includeMic) {
      if (mic) {
        if (audio.inputDevice?.deviceId !== mic.deviceId) {
          try {
            await audio.setInputDevice(mic.deviceId)
          } catch (e) {
            console.warn('[TwilioDevice] could not use the chosen microphone:', e)
          }
        }
      } else {
        if (prefs.audioDeviceId) console.warn('[TwilioDevice] chosen microphone is not connected; using the system default')
        // The mic in use was unplugged mid-call: move the call to the default
        // instead of leaving it on a dead device. The saved choice stays, so
        // the call moves back when it's plugged in again.
        const current = audio.inputDevice?.deviceId
        if (current && current !== 'default' && !inputs?.has(current) && inputs?.has('default')) {
          try {
            await audio.setInputDevice('default')
          } catch (e) {
            console.warn('[TwilioDevice] could not switch to the default microphone:', e)
          }
        }
      }
    }
    this._setMicStatus({
      inUse: audio.inputDevice?.label || inputs?.get('default')?.label || '',
      missing: prefs.audioDeviceId && !mic ? prefs.audioDeviceLabel || 'Your saved microphone' : '',
    })
  }

  /**
   * Which mic calls are using, and the saved one when it isn't connected.
   * The call screens show both, so a call quietly on the laptop mic or a
   * speakerphone is visible.
   */
  get micStatus() {
    return this._micStatus || { inUse: '', missing: '' }
  }

  _setMicStatus(status) {
    const prev = this.micStatus
    if (prev.inUse === status.inUse && prev.missing === status.missing) return
    this._micStatus = status
    this._emit('micStatusChanged', status)
  }

  /** Stops holding the mic open between calls. */
  _releaseMic() {
    const audio = this.device?.audio
    if (!audio?.inputDevice) return
    audio.unsetInputDevice().catch((e) => console.warn('[TwilioDevice] could not release the microphone:', e))
  }

  // ─── Call controls ──────────────────────────────────────────────────────

  toggleMute() {
    if (!this.activeCall) return false
    const muted = !this.activeCall.isMuted()
    this.activeCall.mute(muted)
    this._emit('muteChanged', muted)
    return muted
  }

  /** @param {string} digits */
  sendDigits(digits) {
    if (!this.activeCall) return
    this.activeCall.sendDigits(digits)
  }

  disconnect() {
    if (this.activeCall) {
      this.activeCall.disconnect()
      this.activeCall = null
      this.currentCallContext = null
    }
  }

  // ─── Media streams (for TranscriptionEngine) ────────────────────────────

  getLocalStream() {
    return this.activeCall?.getLocalStream() ?? null
  }

  getRemoteStream() {
    return this.activeCall?.getRemoteStream() ?? null
  }

  // ─── Teardown ───────────────────────────────────────────────────────────

  /**
   * Logged out (by clicking Log out or because the session expired): forget the
   * saved "available" choice and disconnect every open tab, so the next person
   * to log in on this browser starts offline.
   */
  endSession() {
    phoneAvailabilityStore.save({ available: false })
    this._broadcastAvailabilityIntent(false)
  }

  async destroy() {
    // destroy() runs when the user logs out (App.vue clears current_user).
    this.endSession()
    this._isDestroying = true
    this.disconnect()
    this.stopVoicemailPolling()
    window.removeEventListener('storage', this._onStorageEvent)
    window.removeEventListener('online', this._onOnline)
    window.removeEventListener('pagehide', this._onPageHide)
    clearInterval(this._presenceTimer)
    this._presenceTimer = null
    this._onPageHide()
    this._peers.clear()
    this._clearRecoveryTimer()
    if (this.device) {
      this.device.unregister()
      this.device.destroy()
      this.device = null
    }
    this._initialized = false
    this._registered = false
    this._setStatus('offline')
    this._unreadVoicemails = 0
    this.identity = null
    this.currentCallContext = null
    this._listeners = {}
  }

  get isInitialized() {
    return this._initialized
  }

  get isRegistered() {
    return this._registered
  }

  get isBusy() {
    return this.activeCall !== null
  }
}

export const twilioDevice = new TwilioDeviceService()
